/**
 * ============================================================================
 * RecentPayoutsCard - LAST 3 PAYOUTS DISPLAY
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE CLAUDE SESSIONS:
 *
 * 1. SHOW ONLY LAST 3
 *    - Use .slice(0, 3) on payoutHistory
 *    - "View All" link goes to /payouts page
 *    - Keep this component lightweight
 *
 * 2. STATUS COLORS
 *    - completed: Green
 *    - processing: Blue
 *    - pending: Yellow
 *    - failed: Red
 *    - Use getPayoutStatusColor from formatters
 *
 * 3. EMPTY STATE
 *    - Show when no payout history
 *    - Friendly message, not alarming
 *    - Icon + text
 *
 * ============================================================================
 */

import React from 'react';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { formatCurrency, formatDate, getPayoutStatusColor } from '../../utils/revenueFormatters';
import type { PayoutHistory } from '../../types/revenue';

interface RecentPayoutsCardProps {
  payoutHistory: PayoutHistory[];
  onViewAll: () => void;
}

/**
 * Recent payouts card showing last 3 payouts
 */
export function RecentPayoutsCard({ payoutHistory, onViewAll }: RecentPayoutsCardProps) {
  // Show only last 3
  const recentPayouts = payoutHistory.slice(0, 3);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Recent Payouts</h3>
          <p className="text-sm text-gray-600">Last 3 payouts</p>
        </div>
        {payoutHistory.length > 0 && (
          <button
            onClick={onViewAll}
            className="text-blue-600 font-semibold text-sm hover:text-blue-700 flex items-center transition-colors"
          >
            View All <ArrowRight className="w-4 h-4 ml-1" />
          </button>
        )}
      </div>

      {recentPayouts.length > 0 ? (
        <div className="space-y-3">
          {recentPayouts.map((payout) => {
            const statusColorClass = getPayoutStatusColor(payout.status);

            return (
              <div
                key={payout.id}
                className="bg-gray-50 border border-gray-200 rounded-lg p-3 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <div
                      className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        payout.status === 'completed'
                          ? 'bg-green-100'
                          : payout.status === 'processing'
                          ? 'bg-blue-100'
                          : 'bg-gray-100'
                      }`}
                    >
                      <CheckCircle
                        className={`w-5 h-5 ${
                          payout.status === 'completed'
                            ? 'text-green-600'
                            : payout.status === 'processing'
                            ? 'text-blue-600'
                            : 'text-gray-600'
                        }`}
                      />
                    </div>
                    <div>
                      <p className="font-bold text-gray-900">
                        {formatCurrency(payout.net_payout)}
                      </p>
                      <p className="text-xs text-gray-500">Bank Transfer</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900 text-sm">
                      {formatDate(payout.created_at)}
                    </p>
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${statusColorClass}`}>
                      {payout.status}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-gray-300">
                  <p className="text-xs text-gray-500 font-mono">
                    {payout.transfer_reference || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-600">
                    {payout.total_bookings || payout.booking_count || 0} bookings
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        // Empty state
        <div className="text-center py-8">
          <CheckCircle className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600">No payout history yet</p>
          <p className="text-sm text-gray-500 mt-1">Your completed payouts will appear here</p>
        </div>
      )}
    </div>
  );
}
